import React, { useState, useRef } from "react";
import CustomTooltip from "./Filters/CustomTooltip";
import DocumentTypeIcons from "./Filters/CustomDocumentTypeIcons";
import {
  CheckCircleIcon,
  CheckBadgeIcon,
  ChevronDownIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import MenuIcon from "../assets/images/menu.png";
import { useScreenWidth } from "../custom-hooks/useScreenWidth";
import { useDispatch, useSelector } from "react-redux";
import { setDrawer } from "../redux-store/features/layoutSlice";
import { Menu } from "@headlessui/react";
import Capabilities from "./Capabilities";
import ServiceCategoryTooltip from "./ServiceCategoryTooltip";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { StatusBadges } from "./StatusBadges";
import {
  dateFormat,
  getCityStateCountry,
  isDiligentAdminUserRole,
  useUserRole,
} from "../utils/helperMethods";
import { Tooltip } from "@mui/material";
import { tooltipContent } from "../utils/constants";
import { useLoadScript } from "@react-google-maps/api";
import { MenuDropdownAccordingToStatuses } from "./RFIsComponents/MenuDropdownAccordingToStatuses";
import DocumentLibraryActivityLog from "./LibraryRequests/DocumentLibraryActivityLog";
import { useGetDocumentActivity } from "../custom-hooks/queries";
import { findRfiColorMeta } from "../utils/serviceCategoriesColors";
import {
  DEFAULT_RFI_BADGE_STYLE,
  getRfiCapabilityBadgeDisplayName,
} from "../utils/rfiCatalogServiceNames";
import { getDocumentCardDate } from "../utils/documentCardDate";

const ProviderDetailCard = ({
  documentType,
  name,
  services,
  publishedDate,
  assessmentDate,
  status,
  onClick,
  docLocation,
  docLocationFull,
  requestedDate,
  documentName,
  entityId,
  docId,
  questionnaireId,
  sponsorRfiResponseId,
  details,
  isLegacyRfi,
  v1_doc_id,
  v1_doc_name,
  hideRequestActions = false,
  additionalScope,
  libraryRefetch,
  archiveDocumentContext = "library",
}) => {
  const screenWidth = useScreenWidth();
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showChevron, setShowChevron] = useState(false);
  const capabilitiesRef = useRef(null);
  const lowerCaseDocType = documentType?.toLowerCase();
  const {
    label: dateLabel,
    value: dateValue,
    fallback: dateFallback,
  } = getDocumentCardDate({
    documentType,
    status,
    publishedDate,
    requestedDate,
    assessmentDate,
  });

  const toggleAccordion = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const location = useLocation();
  const { rfis } = useSelector((state) => state.rfisColors);
  const userRole = useUserRole();
  const isCatalogAdmin = isDiligentAdminUserRole(userRole);
  const isArchivedCard = status === "Archived";
  const { data: activityData, isLoading: activityLoading } = useGetDocumentActivity(
    docId,
    isCatalogAdmin && Boolean(docId)
  );
  // Kept for activity-log fallback only (comment renders inside the archive entry).
  const archiveCommentText =
    details?.archive_comment ?? details?.archiveComment ?? "";
  const archivedBy =
    details?.archived_by ?? details?.archivedBy ?? "";
  const archivedOn =
    details?.archived_on ?? details?.archivedOn ?? null;

  const rfiSingleBadgeStyle = (() => {
    if (!documentName) return DEFAULT_RFI_BADGE_STYLE;
    const meta = findRfiColorMeta(documentName, rfis);
    if (meta) {
      return {
        bgColor: meta.bg,
        textColor: meta.text,
        borderColor: meta.border,
      };
    }
    return DEFAULT_RFI_BADGE_STYLE;
  })();

  return (
    <div
      className={`rounded-2xl border h-auto lg:w-96 custom-breakpoint-3:w-80 custom-breakpoint-4:w-72 transition-all duration-300 ${
        isArchivedCard
          ? "bg-slate-50/80 border-slate-200 opacity-90"
          : "bg-white"
      }`}
    >
      {/* Grey area (expandable) */}
      <div
        className={`${
          isArchivedCard ? "bg-slate-100/70" : "bg-gray-50"
        } w-full ${
          isExpanded ? "h-auto" : "h-40"
        } rounded-t-2xl flex justify-center items-center cursor-pointer transition-all duration-300`}
      >
        <div
          onClick={onClick}
          className={`${
            isArchivedCard ? "bg-slate-100/70" : "bg-gray-50"
          } w-full rounded-t-2xl p-4`}
        >
          <div
            className={`flex space-x-3 ${isExpanded ? "h-auto" : "h-[8rem]"}`}
          >
            <DocumentTypeIcons documentType={documentType} />
            <div className="w-3/4	">
              <div className="flex justify-between">
                <span
                  className={`text-gray-900 font-semibold w-80 ${
                    lowerCaseDocType === "vqa" && services?.length > 0 ? "" : ""
                  }`}
                >
                  <CustomTooltip cutOffAfter={30} sliceLength={30}>
                    {name}
                  </CustomTooltip>
                </span>

                {(!hideRequestActions || isCatalogAdmin) && (
                  <MenuDropdownAccordingToStatuses
                    status={status}
                    type={documentType}
                    drawerOpen={onClick}
                    docId={docId}
                    isDocLibrary={true}
                    isMyRFI={false}
                    questionnaireId={questionnaireId}
                    sponsorRfiResponseId={entityId}
                    details={details}
                    isLegacyRfi={isLegacyRfi}
                    v1_doc_id={v1_doc_id}
                    v1_doc_name={v1_doc_name}
                    hideRequestActions={hideRequestActions}
                    libraryRefetch={libraryRefetch}
                    archiveDocumentContext={archiveDocumentContext}
                  />
                )}
              </div>
              {lowerCaseDocType === "vqa" &&
                (services?.length > 0 || additionalScope?.length > 0) && (
                <div
                  className={`flex justify-between mt-2 mb-2 overflow-x-hidden ${
                    isExpanded
                      ? "max-h-full overflow-y-auto"
                      : "max-h-15 overflow-y-hidden"
                  }`}
                  ref={capabilitiesRef}
                >
                  <div>
                    <Capabilities
                      services={services || []}
                      isProviderDetail={true}
                      icon={true}
                      isExpanded={isExpanded}
                      setShowChevron={setShowChevron}
                      showLimited={true}
                      name={name}
                      additionalScopeTags={additionalScope}
                    />
                  </div>
                  {showChevron && (
                    <div
                      onClick={toggleAccordion}
                      className="cursor-pointer ml-3"
                    >
                      <ChevronDownIcon
                        className={`w-4 h-4 transform transition-transform ${
                          isExpanded ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    </div>
                  )}
                </div>
              )}
              {lowerCaseDocType === "rfi" && services?.length > 0 && (
                <div
                  className={`flex justify-between mt-2 mb-2 overflow-x-hidden ${
                    isExpanded
                      ? "max-h-full overflow-y-auto"
                      : "max-h-15 overflow-y-hidden"
                  }`}
                  ref={capabilitiesRef}
                >
                  <div>
                    <Capabilities
                      services={services}
                      isProviderDetail={true}
                      icon={true}
                      isExpanded={isExpanded}
                      setShowChevron={setShowChevron}
                      showLimited={true}
                      name={name}
                      applyRfiCatalogCustomLabels={true}
                    />
                  </div>
                  {showChevron && (
                    <div
                      onClick={toggleAccordion}
                      className="cursor-pointer ml-3"
                    >
                      <ChevronDownIcon
                        className={`w-4 h-4 transform transition-transform ${
                          isExpanded ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    </div>
                  )}
                </div>
              )}
              {lowerCaseDocType === "rfi" &&
                (!services || services.length === 0) && (
                  <div
                    className={`flex justify-between mt-2 mb-2 overflow-x-hidden`}
                    ref={capabilitiesRef}
                  >
                    <div
                      className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium "
                      style={{
                        backgroundColor: rfiSingleBadgeStyle.bgColor,
                        boxShadow: `inset 0 0 0 1px ${rfiSingleBadgeStyle.borderColor}1A`,
                        color: rfiSingleBadgeStyle.textColor,
                      }}
                    >
                      {getRfiCapabilityBadgeDisplayName(documentName)}
                      {documentName === "Core Capabilities" ? (
                        <Tooltip
                          title={tooltipContent}
                          arrow
                          placement="bottom"
                          sx={{
                            backgroundColor: "#555555",
                            color: "white",
                            fontSize: "1rem",
                          }}
                        >
                          <span>
                            <InformationCircleIcon className="w-5 h-5 text-center ml-2" />
                          </span>
                        </Tooltip>
                      ) : (
                        <ServiceCategoryTooltip name={documentName} />
                      )}
                    </div>
                    {showChevron && (
                      <div
                        onClick={toggleAccordion}
                        className="cursor-pointer ml-3"
                      >
                        <ChevronDownIcon
                          className={`w-4 h-4 transform transition-transform ${
                            isExpanded ? "rotate-180" : "rotate-0"
                          }`}
                        />
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 flex flex-col space-y-4">
        <div className="flex justify-between items-center border-b pb-2 mt-2">
          <dt className="text-sm font-semibold text-custom-dark-grey text-left">
            Document Type
          </dt>
          <dd
            className="inline-flex cursor-default items-center gap-x-3 rounded-md text-white py-1 px-2 text-sm font-semibold shadow-sm"
            style={{
              justifyContent: "space-between",
              alignItems: "center",
              width: "80px",
              backgroundColor:
                lowerCaseDocType === "rfi"
                  ? "rgb(79, 70, 229)"
                  : "rgb(225, 29, 72)",
            }}
          >
            <p className="flex-1 text-center ">
              {documentType === "VQA" ? "Audit" : documentType === "RFI" ? "VQQ" : documentType}
            </p>
            <p>
              {lowerCaseDocType === "rfi" ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                <CheckBadgeIcon className="h-5 w-5" />
              )}
            </p>
          </dd>
        </div>
        {/* Additional Details */}
        <div className="flex justify-between items-center border-b pb-2">
          <dt className="text-sm font-semibold text-custom-dark-grey text-left">
            {dateLabel}
          </dt>
          <dd className="font-normal text-sm text-right">
            {dateValue !== null ? dateFormat(dateValue) : dateFallback}
          </dd>
        </div>
        {docLocation && (
          <div className="flex justify-between items-center border-b pb-2">
            <dt className="text-sm font-semibold text-custom-dark-grey text-left">
              Location
            </dt>
            <dd className="text-sm">
              <CustomTooltip forceTooltip={true} toolTipContent={docLocationFull}>
                {docLocation}
              </CustomTooltip>
            </dd>
          </div>
        )}
        <div className="flex justify-between items-center pb-2">
          <dt className="text-sm font-semibold text-custom-dark-grey text-left">
            Status
          </dt>
          <dd>
            <StatusBadges statusName={status} />
          </dd>
        </div>
        {isCatalogAdmin ? (
          <DocumentLibraryActivityLog
            items={activityData?.items}
            activityNavigation={activityData}
            loading={activityLoading}
            compact
            entityType={documentType}
            entityId={entityId}
            hcQuestionnaireId={questionnaireId ?? details?.hc_questionnaire_id}
            vqaRequestId={details?.vqa_request_id}
            providerName={name}
            documentStatus={status}
            archiveComment={archiveCommentText}
            archivedBy={archivedBy}
            archivedOn={archivedOn}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ProviderDetailCard;
